const SCALAR_LINKS = ['fatherId', 'motherId'];
const ARRAY_LINKS = ['spouseIds', 'crossLevelSpouseIds'];

import { getProbandState } from './pedigree/probandState';

// The STRUCTURAL root (the proBandId-carrying row, pid 1) can never migrate out
// of its own family: it owns the family's identity, and PedigreeConfig.user_id
// points at it. Moving or deleting it leaves the source family unreadable.
//
// This must be a STRUCTURAL test, not a designation test. Designation is
// three-state — once a clinician runs Remove Proband there is no isProband:true
// row at all, so a designation-only check computes an empty proband component
// and every member, root included, falls through as eligible.
const isStructuralRoot = (node) => !!node && (!!node.proBandId || node.pid === 1);

const isRealMember = (node) =>
  !!node &&
  !node.deleted &&
  !node.hidden &&
  !node.isProxy &&
  !isStructuralRoot(node) &&
  (!!node.familyMemberId || !!node.upn);

const addEdge = (adj, a, b) => {
  if (a === undefined || a === null || a === '') return;
  if (b === undefined || b === null || b === '') return;
  const ka = String(a);
  const kb = String(b);
  if (ka === kb) return;
  if (!adj.has(ka)) adj.set(ka, new Set());
  if (!adj.has(kb)) adj.set(kb, new Set());
  adj.get(ka).add(kb);
  adj.get(kb).add(ka);
};

const isActiveNode = (node) =>
  !!node &&
  node.pid !== undefined &&
  node.pid !== null &&
  !node.deleted &&
  !node.hidden;

export const buildAdjacency = (members) => {
  const adj = new Map();
  const activeNodesByPid = new Map();
  const activePidSet = new Set(
    (members || []).filter(isActiveNode).map((node) => {
      const pid = String(node.pid);
      activeNodesByPid.set(pid, node);
      return pid;
    })
  );

  const hasReciprocalSpouseLikeLink = (fromPid, toPid) => {
    const target = activeNodesByPid.get(String(toPid));
    if (!target) return false;
    const spouseIds = Array.isArray(target.spouseIds) ? target.spouseIds : [];
    const crossLevelSpouseIds = Array.isArray(target.crossLevelSpouseIds)
      ? target.crossLevelSpouseIds
      : [];
    const from = String(fromPid);
    return (
      spouseIds.some((v) => String(v) === from) ||
      crossLevelSpouseIds.some((v) => String(v) === from)
    );
  };

  (members || []).forEach((node) => {
    if (!isActiveNode(node)) return;
    const pid = String(node.pid);
    if (!adj.has(pid)) adj.set(pid, new Set());
    SCALAR_LINKS.forEach((f) => {
      const target = node[f];
      if (!activePidSet.has(String(target))) return;
      addEdge(adj, pid, target);
    });
    ARRAY_LINKS.forEach((f) => {
      if (!Array.isArray(node[f])) return;
      node[f].forEach((v) => {
        if (!activePidSet.has(String(v))) return;
        if (!hasReciprocalSpouseLikeLink(pid, v)) return;
        addEdge(adj, pid, v);
      });
    });
  });
  return adj;
};

const bfs = (adj, seeds) => {
  const seen = new Set();
  const stack = seeds.map((p) => String(p));
  while (stack.length) {
    const cur = stack.pop();
    if (seen.has(cur)) continue;
    seen.add(cur);
    const neighbours = adj.get(cur);
    if (neighbours) neighbours.forEach((n) => !seen.has(n) && stack.push(n));
  }
  return seen;
};

// The proband's connected component (pid strings).
export const getProbandComponent = (members) => {
  const { probandPid } = getProbandState(members);
  if (probandPid === undefined || probandPid === null) return new Set();
  return bfs(buildAdjacency(members), [probandPid]);
};

// pid strings of members eligible to migrate: real, non-proband, and not in
// the proband's connected component.
export const getEligiblePids = (members) => {
  const { probandPid } = getProbandState(members);
  const activeProbandPid =
    probandPid === undefined || probandPid === null ? null : String(probandPid);
  const probandComponent = getProbandComponent(members);
  const eligible = new Set();
  (members || []).forEach((node) => {
    if (!isRealMember(node)) return;
    if (activeProbandPid !== null && String(node.pid) === activeProbandPid)
      return;
    if (probandComponent.has(String(node.pid))) return;
    eligible.add(String(node.pid));
  });
  return eligible;
};

// Full connected component containing `pid` (pid strings).
export const getComponentOf = (members, pid) =>
  bfs(buildAdjacency(members), [pid]);

// A selection is fully independent when its closure equals the selection.
export const isFullyIndependentSelection = (members, selectedPidSet) => {
  const selected = new Set([...selectedPidSet].map((p) => String(p)));
  if (selected.size === 0) return { independent: false, danglingPids: [] };
  const closure = bfs(buildAdjacency(members), [...selected]);
  const danglingPids = [...closure].filter((p) => !selected.has(p));
  return { independent: danglingPids.length === 0, danglingPids };
};

export const getMigratablePidsDetail = (members, selectedPidSet) => {
  const eligible = getEligiblePids(members);
  const selected = new Set([...selectedPidSet].map((p) => String(p)));
  const allEligible =
    selected.size > 0 && [...selected].every((p) => eligible.has(p));
  const { independent, danglingPids } = isFullyIndependentSelection(
    members,
    selected
  );
  return {
    eligible,
    allEligible,
    independent: independent && allEligible,
    danglingPids
  };
};
